import {
  Box,
  Divider,
  Switch,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { CubeMeasureEnum } from '../../../services/data/impl/cube/cubeSlice';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { flexCenter } from '../../commonUI/cssClasses';
import { selectUseCustomColor, setUseCustomColor } from '../../map/mapSlice';
import { selectCurrMeasure } from '../../select/selectSlice';
import CustomHSVColorSettings from './CustomHSVColorSettings';
import HueRotationSlider from './HueRotationSlider';
const commonToggleWithLabelStyles: SxProps = {
  width: '100%',
  ...flexCenter,
  flexDirection: 'column',
  m: '0.25rem 0',
};

const ColorSettingsPopup: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const useCustomColor = useAppSelector(selectUseCustomColor);
  const currMeasure = useAppSelector(selectCurrMeasure);
  const { t } = useTranslation();

  // Simplified color setting for customers.  This just toggles custom saturation on/off and resets the default hsv saturation/values in the mapSlice.  In QA mode, bloomfield users can adjust these settings independently.
  const handleUseCustomColorToggleClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setUseCustomColor(event.target.checked));
  };

  const titleStyles: SxProps = {
    width: '100%',
    ...flexCenter,
    pb: '0.5rem',
    mt: '0.4rem',
    color: theme.colors.darkTheme.grey.muted,
  };

  const dividerElement = (
    <Divider
      sx={{
        width: '100%',
        bgcolor: theme.colors.darkTheme.grey.muted,
        mb: '1rem',
      }}
    />
  );

  const customColorContent = (
    <Box sx={{ ...commonToggleWithLabelStyles }}>
      <Typography noWrap>
        {t('Realistic / Exaggerated', { ns: 'ranch-view' })}
      </Typography>
      <Switch
        checked={useCustomColor}
        onChange={handleUseCustomColorToggleClick}
        inputProps={{ 'aria-label': 'Color Switch' }}
        color='primary'
        disabled={currMeasure !== CubeMeasureEnum.DetectionFactHasColorHSV}
      />
    </Box>
  );

  const colorSection = (
    <Box
      sx={{
        width: '100%',
        ...flexCenter,
        flexDirection: 'column',
      }}
    >
      <Typography variant='h6' sx={{ ...titleStyles, mt: 0 }}>
        {t('Basic Color Settings', { ns: 'ranch-view' })}
      </Typography>
      {dividerElement}
      <Tooltip
        title={t(
          'Rotates the start / end point of the colors on the chart.  This is purely an aesthetic change to help visualize the color / maturity path of your data since color is a circle and can start at any point.',
          { ns: 'ranch-view' }
        )}
        placement='top'
      >
        <Box sx={{ width: '100%', ...flexCenter, flexDirection: 'column' }}>
          <Typography noWrap>
            {t('Color Start Point', { ns: 'ranch-view' })}
          </Typography>
          <HueRotationSlider />
        </Box>
      </Tooltip>
      {currMeasure === CubeMeasureEnum.DetectionFactHasColorHSV ? (
        customColorContent
      ) : (
        <Tooltip
          title={t(
            '"Color" must be selected in the measure dropdown for this to impact colors.',
            { ns: 'ranch-view' }
          )}
          placement='top'
        >
          <span>{customColorContent}</span>
        </Tooltip>
      )}
      <Typography variant='h6' sx={{ ...titleStyles }}>
        {t('Advanced Color Settings', { ns: 'ranch-view' })}
      </Typography>
      {dividerElement}
      <CustomHSVColorSettings
        commonToggleWithLabelStyles={commonToggleWithLabelStyles}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: '1rem',
      }}
    >
      {colorSection}
    </Box>
  );
};

export default ColorSettingsPopup;
